// @flow
import { getAuthToken, getBidderId } from '../modules/user';
import api from '../api/conversation';
import { createSelector } from 'reselect';
import { getDeployment } from '../modules/config';
import type { GlobalState } from '../rootReducer';
import ms from 'ms';

/* Action Types */
export const LOAD_CONVERSATIONS_FAIL = 'la/domain/conversations/LOAD_FAIL';
export const LOAD_CONVERSATIONS_REQUEST = 'la/domain/conversations/LOAD_REQUEST';
export const LOAD_CONVERSATIONS_SUCCESS = 'la/domain/conversations/LOAD_SUCCESS';

export const CONVERSATIONS_ACTION_FAIL = 'la/domain/conversations/ACTION_FAIL';
export const CONVERSATIONS_ACTION_REQUEST = 'la/domain/conversations/ACTION_REQUEST';
export const CONVERSATIONS_ACTION_SUCCESS = 'la/domain/conversations/ACTION_SUCCESS';

const REDUX_STORE_TIME = ms('0s');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: 0,
    loading: false,
    pagination: {
        page: 1,
        pageSize: 48,
        totalPages: 1,
        totalResults: 0,
    },
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let existingPagination;

    switch (action.type) {
        case LOAD_CONVERSATIONS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_CONVERSATIONS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_CONVERSATIONS_SUCCESS:
        case CONVERSATIONS_ACTION_SUCCESS:
            existing = {};
            existingPagination = { ...state.pagination };

            if (action.payload.payload) {
                action.payload.payload.forEach(convo => {
                    existing[convo.conversationId] = { ...convo };
                });
            }

            if (action.payload?.meta?.pagination) {
                const { pagination } = action.payload.meta;
                existingPagination = {
                    page: pagination.pageNumber,
                    pageSize: pagination.pageSize,
                    totalPages: pagination.totalPages,
                    totalResults: pagination.totalResults,
                };
            }

            return {
                ...state,
                byId: existing,
                loaded: action.meta.actionTime,
                loading: false,
                pagination: existingPagination,
            };
        default:
            return state;
    }
}

const stateSelector = (state: GlobalState) => state.conversation;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getConversationPagination = createSelector(stateSelector, state => state.pagination);

export const getConversations = createSelector(byIdSelector, byId => Object.keys(byId).map(conv => byId[conv]));

export const getConversation = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

export const getLoadTimeForConversation = loadedSelector;

export const getConversationLoading = loadingSelector;

const shouldFetch = createSelector([loadedSelector, loadingSelector], (loaded, loading) => {
    const time = Date.now();
    const diff = time - loaded;
    if (diff < REDUX_STORE_TIME) return false;
    return !loading;
});

const shouldFetchConversation = createSelector(
    [getConversation, getLoadTimeForConversation, getConversationLoading],
    (conversation, loaded, loading) => {
        if (conversation?.conversationId) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) return false;
        }
        return !loading;
    }
);

type ConversationQueryType = $ReadOnly<{|
    filter?: string,
    folderId: number,
    page?: number,
    pageSize?: number,
    // sort: string,
|}>;

const loadConversations = (query: ConversationQueryType) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: LOAD_CONVERSATIONS_REQUEST,
        });
        const response = await api.fetchConversations({ authToken, bidderId, deployment, query });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response,
            type: LOAD_CONVERSATIONS_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: LOAD_CONVERSATIONS_FAIL,
        });
    }
};

const loadConversation = (conversationId: number) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: LOAD_CONVERSATIONS_REQUEST,
        });
        const response = await api.fetchConversation({ authToken, bidderId, conversationId, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response,
            type: LOAD_CONVERSATIONS_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: LOAD_CONVERSATIONS_FAIL,
        });
    }
};

export const fetchConversationsIfNeeded = (query: ConversationQueryType) => async (
    dispatch: Function,
    getState: Function
) => {
    if (shouldFetch(getState())) {
        return dispatch(loadConversations(query));
    }
    return Promise.resolve();
};

export const fetchConversationIfNeeded = (conversationId: number) => async (dispatch: Function, getState: Function) => {
    if (shouldFetchConversation(getState(), conversationId)) {
        return dispatch(loadConversation(conversationId));
    }
    return Promise.resolve();
};

const actions = {
    markRead: api.markConversationRead,
    markUnread: api.markConversationUnread,
    move: api.moveConversation,
    trash: api.trashConversations,
};

export const conversationAction = (conversations: any[], action: string, folderId?: number) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: CONVERSATIONS_ACTION_REQUEST,
        });
        const response = await actions[action]({ authToken, bidderId, conversations, deployment, folderId });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response,
            type: CONVERSATIONS_ACTION_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: CONVERSATIONS_ACTION_FAIL,
        });
    }
};
